// Requests.jsx - Complete Updated Version

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  RequestGet,
  RequestUpdate,
  RequestDelete
} from "../../Api/CoreApi";
import {
  FaInbox, FaPaperPlane, FaCheck, FaTimes, FaUser,
  FaMapMarkerAlt, FaGraduationCap, FaBriefcase, FaSearch,
  FaClock, FaHeart, FaEye, FaComment, FaArrowLeft, FaArrowRight,
  FaCheckCircle, FaTimesCircle, FaTrash, FaShare, FaSpinner
} from 'react-icons/fa';
import { message } from 'antd';

const Requests = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [requestsData, setRequestsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const userId = localStorage.getItem('user_id');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await RequestGet();
      console.log("📦 Requests Data:", data);
      setRequestsData(data || []);
    } catch (error) {
      console.error("Error loading requests:", error);
      message.error("Failed to load requests");
      setRequestsData([]);
    } finally {
      setLoading(false);
    }
  };

  // ===== ACCEPT REQUEST =====
  const handleAccept = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: 'accept' }));
    try {
      const response = await RequestUpdate(id, { status: "accepted" });
      console.log("✅ Request accepted:", response);
      message.success("💖 Request accepted! You can now chat.");
      await loadRequests();
    } catch (error) {
      console.error("❌ Accept error:", error);
      message.error("Failed to accept request");
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  // ===== REJECT REQUEST =====
  const handleReject = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: 'reject' }));
    try {
      const response = await RequestUpdate(id, { status: "rejected" });
      console.log("❌ Request rejected:", response);
      message.info("Request rejected");
      await loadRequests();
    } catch (error) {
      console.error("❌ Reject error:", error);
      message.error("Failed to reject request");
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  // ===== DELETE/WITHDRAW REQUEST =====
  const handleDelete = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: 'delete' }));
    try {
      await RequestDelete(id);
      message.info("Request withdrawn");
      await loadRequests();
    } catch (error) {
      console.error("❌ Delete error:", error);
      message.error("Failed to withdraw request");
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  // ===== START CHAT =====
  const handleStartChat = (userId) => {
    if (!userId) {
      message.error("User ID not found");
      return;
    }
    navigate(`/messages?chat=${userId}`);
  };

  // ===== VIEW PROFILE =====
  const handleViewProfile = (userId) => {
    if (!userId) {
      message.error("User ID not found");
      return;
    }
    navigate(`/user-profile/${userId}`);
  };

  // ===== STATS =====
  const stats = {
    received: requestsData.filter(r => r.type === 'received').length,
    sent: requestsData.filter(r => r.type === 'sent').length,
    pending: requestsData.filter(r => r.status === 'pending').length,
    accepted: requestsData.filter(r => r.status === 'accepted').length
  };

  // ===== FILTER DATA =====
  const filteredRequests = requestsData.filter(item => {
    // Tab filter
    if (activeTab === 'received' && item.type !== 'received') return false;
    if (activeTab === 'sent' && item.type !== 'sent') return false;
    
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      item.name?.toLowerCase().includes(searchLower) ||
       item.bid?.toLowerCase().includes(searchLower) ||
    (item.profile_id && item.profile_id.toLowerCase().includes(searchLower)) || 
      item.location?.toLowerCase().includes(searchLower);
      
    
    // Status filter
    if (filterBy === 'pending' && item.status !== 'pending') return false;
    if (filterBy === 'accepted' && item.status !== 'accepted') return false;
    if (filterBy === 'rejected' && item.status !== 'rejected') return false;
    
    return matchesSearch;
  });

  // ===== GET STATUS BADGE =====
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': 
        return { class: 'pending', icon: <FaClock size={11} />, label: 'Pending' };
      case 'accepted': 
        return { class: 'accepted', icon: <FaCheckCircle size={11} />, label: 'Accepted ✅' };
      case 'rejected': 
        return { class: 'rejected', icon: <FaTimesCircle size={11} />, label: 'Rejected ❌' };
      default: 
        return { class: 'pending', icon: null, label: 'Unknown' };
    }
  };

  // ===== LOADING =====
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        flexDirection: 'column',
        gap: 16
      }}>
        <FaSpinner size={48} style={{ color: '#ff0055', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#888' }}>Loading requests...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .rq-page { background: #fff; min-height: 100vh; padding: 20px 16px; }
        .rq-wrap { max-width: 1280px; margin: 0 auto; }

        .rq-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 22px; flex-wrap: wrap; gap: 12px; }
        .rq-title { font-size: 24px; font-weight: 800; color: #1a0a10; margin: 0; display: flex; align-items: center; gap: 10px; }
        .rq-title svg { color: #ff0055; }
        .rq-subtitle { color: #888; font-size: 13.5px; margin: 4px 0 0; }

        .rq-search-wrap { position: relative; }
        .rq-search { padding: 10px 14px 10px 40px; border: 1.5px solid #ffe4ec; border-radius: 12px; font-size: 13px; outline: none; width: 260px; transition: all .3s ease; background: #fff; }
        .rq-search:focus { border-color: #ff0055; box-shadow: 0 0 0 3px rgba(255,0,85,.08); }
        .rq-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #ff4d88; font-size: 13px; }

        .rq-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 22px; }
        .rq-stat-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 16px; padding: 16px; display: flex; align-items: center; gap: 12px; transition: all .3s ease; box-shadow: 0 4px 14px rgba(255,0,85,.04); }
        .rq-stat-card:hover { transform: translateY(-3px); border-color: #ffb3c8; box-shadow: 0 10px 24px rgba(255,0,85,.1); }
        .rq-stat-icon { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; color: #fff; flex-shrink: 0; }
        .rq-stat-icon.received { background: linear-gradient(135deg, #ff0055, #ff4d88); }
        .rq-stat-icon.sent { background: linear-gradient(135deg, #ff4d88, #ff80aa); }
        .rq-stat-icon.pending { background: linear-gradient(135deg, #ff9800, #ffb74d); }
        .rq-stat-icon.accepted { background: linear-gradient(135deg, #4caf50, #66bb6a); }
        .rq-stat-value { font-size: 22px; font-weight: 800; color: #1a0a10; margin: 0; }
        .rq-stat-label { font-size: 12px; color: #888; margin: 2px 0 0; font-weight: 500; }

        .rq-tabs { display: flex; gap: 4px; padding: 5px; background: #fff0f3; border: 2px solid #ffe4ec; border-radius: 14px; margin-bottom: 16px; flex-wrap: wrap; }
        .rq-tab { padding: 9px 18px; border: none; border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 700; transition: all .25s ease; background: transparent; color: #888; display: flex; align-items: center; gap: 6px; }
        .rq-tab:hover { color: #ff0055; }
        .rq-tab.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; box-shadow: 0 4px 14px rgba(255,0,85,.2); }
        .rq-tab-badge { background: rgba(255,255,255,.25); color: #fff; font-size: 10.5px; padding: 2px 8px; border-radius: 999px; font-weight: 700; }
        .rq-tab:not(.active) .rq-tab-badge { background: #ff0055; color: #fff; }

        .rq-filter-group { display: flex; gap: 6px; margin-bottom: 18px; flex-wrap: wrap; }
        .rq-filter-btn { padding: 7px 14px; border: 1.5px solid #ffe4ec; border-radius: 10px; background: #fff; cursor: pointer; font-size: 12.5px; color: #666; font-weight: 600; transition: all .25s ease; display: inline-flex; align-items: center; gap: 5px; }
        .rq-filter-btn:hover { border-color: #ff80aa; color: #ff0055; }
        .rq-filter-btn.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: #ff0055; box-shadow: 0 4px 12px rgba(255,0,85,.18); }

        .rq-grid { display: grid; grid-template-columns: 1fr; gap: 14px; }
        .rq-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 18px; padding: 20px; transition: all .3s ease; box-shadow: 0 4px 14px rgba(255,0,85,.04); }
        .rq-card:hover { transform: translateY(-3px); border-color: #ffb3c8; box-shadow: 0 12px 32px rgba(255,0,85,.1); }

        .rq-card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; flex-wrap: wrap; gap: 10px; }
        .rq-profile { display: flex; align-items: center; gap: 14px; }
        .rq-avatar-wrap { position: relative; }
        .rq-avatar { width: 58px; height: 58px; border-radius: 50%; border: 3px solid #ff0055; box-shadow: 0 6px 16px rgba(255,0,85,.18); object-fit: cover; }
        .rq-online-dot { position: absolute; bottom: 2px; right: 2px; width: 14px; height: 14px; border-radius: 50%; background: #4caf50; border: 2.5px solid #fff; }

        .rq-name { font-size: 17px; font-weight: 800; color: #1a0a10; margin: 0; }
        .rq-bid { font-size: 12px; color: #ff4d88; font-weight: 600; margin: 2px 0; }
        .rq-online-row { display: flex; align-items: center; gap: 5px; }
        .rq-online-text { font-size: 11px; font-weight: 600; }

        .rq-status { display: inline-flex; align-items: center; gap: 5px; padding: 6px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; }
        .rq-status.pending { background: linear-gradient(135deg, #fff3e0, #ffe0b2); color: #e65100; border: 1px solid #ffcc80; }
        .rq-status.accepted { background: linear-gradient(135deg, #e8f5e9, #c8e6c9); color: #2e7d32; border: 1px solid #a5d6a7; }
        .rq-status.rejected { background: linear-gradient(135deg, #fff0f3, #ffe4ec); color: #ff0055; border: 1px solid #ffd3df; }

        .rq-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; padding: 14px 16px; background: linear-gradient(135deg, #fffafc, #fff0f3); border: 1.5px solid #ffe4ec; border-radius: 12px; margin-top: 12px; }
        .rq-detail { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #555; font-weight: 500; }
        .rq-detail svg { color: #ff4d88; flex-shrink: 0; }
        .rq-detail.heart svg { color: #ff0055; }

        .rq-message { margin-top: 12px; padding: 12px 16px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); border: 1.5px solid #ffd3df; border-radius: 12px; font-size: 13px; color: #555; font-style: italic; position: relative; padding-left: 30px; }
        .rq-message::before { content: '"'; position: absolute; left: 10px; top: 0; font-size: 32px; color: #ff0055; font-family: serif; font-weight: 800; line-height: 1; }

        .rq-actions { display: flex; gap: 8px; margin-top: 14px; flex-wrap: wrap; }
        .rq-action-btn { padding: 9px 16px; border: none; border-radius: 10px; font-size: 12.5px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all .25s ease; }
        .rq-action-btn:hover:not(:disabled) { transform: translateY(-2px); }
        .rq-action-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none !important; }
        .rq-btn-accept { background: linear-gradient(135deg, #4caf50, #66bb6a); color: #fff; box-shadow: 0 4px 14px rgba(76,175,80,.22); }
        .rq-btn-accept:hover:not(:disabled) { box-shadow: 0 8px 20px rgba(76,175,80,.3); }
        .rq-btn-reject { background: linear-gradient(135deg, #f44336, #e57373); color: #fff; box-shadow: 0 4px 14px rgba(244,67,54,.22); }
        .rq-btn-reject:hover:not(:disabled) { box-shadow: 0 8px 20px rgba(244,67,54,.3); }
        .rq-btn-chat { background: linear-gradient(135deg, #2196f3, #42a5f5); color: #fff; box-shadow: 0 4px 14px rgba(33,150,243,.22); }
        .rq-btn-chat:hover:not(:disabled) { box-shadow: 0 8px 20px rgba(33,150,243,.3); }
        .rq-btn-view { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; box-shadow: 0 4px 14px rgba(255,0,85,.22); }
        .rq-btn-withdraw { background: linear-gradient(135deg, #ff9800, #ffb74d); color: #fff; box-shadow: 0 4px 14px rgba(255,152,0,.22); }
        .rq-btn-outline { background: #fff; color: #ff0055; border: 1.5px solid #ff0055 !important; }
        .rq-btn-outline:hover:not(:disabled) { background: linear-gradient(135deg, #fff0f3, #ffe4ec); }

        .rq-empty { padding: 60px 24px; text-align: center; background: #fff; border: 2px solid #ffe4ec; border-radius: 20px; }
        .rq-empty-icon { width: 80px; height: 80px; border-radius: 50%; background: linear-gradient(135deg, #fff0f3, #ffe4ec); display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; font-size: 32px; color: #ff0055; }
        .rq-empty-title { font-size: 20px; font-weight: 800; color: #c2185b; margin: 0 0 8px; }
        .rq-empty-sub { color: #888; margin: 0 0 20px; font-size: 13.5px; }
        .rq-empty-btn { padding: 11px 28px; border-radius: 999px; border: none; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: 0 8px 22px rgba(255,0,85,.22); transition: all .3s ease; }
        .rq-empty-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(255,0,85,.3); }

        .rq-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 22px; padding: 16px 4px; flex-wrap: wrap; gap: 10px; }
        .rq-page-info { font-size: 13px; color: #888; }
        .rq-page-info strong { color: #ff0055; font-weight: 800; }
        .rq-page-btns { display: flex; gap: 6px; }
        .rq-page-btn { width: 34px; height: 34px; border-radius: 9px; border: 1.5px solid #ffe4ec; background: #fff; color: #888; cursor: pointer; font-weight: 600; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all .25s ease; }
        .rq-page-btn:hover { border-color: #ff0055; color: #ff0055; }
        .rq-page-btn.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: #ff0055; box-shadow: 0 4px 12px rgba(255,0,85,.2); }

        @media (max-width: 1024px) {
          .rq-stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .rq-header { flex-direction: column; align-items: stretch; }
          .rq-search { width: 100%; }
          .rq-tabs { flex-wrap: wrap; }
          .rq-tab { flex: 1; justify-content: center; }
          .rq-details { grid-template-columns: 1fr; }
        }
        @media (max-width: 480px) {
          .rq-stats-grid { grid-template-columns: 1fr 1fr; }
          .rq-filter-btn { flex: 1; justify-content: center; }
          .rq-profile { flex-direction: column; text-align: center; }
          .rq-actions { justify-content: center; }
        }
      `}</style>

      <div className="rq-page">
        <div className="rq-wrap">

          {/* Header */}
          <div className="rq-header">
            <div>
              <h1 className="rq-title">
                <FaInbox /> Requests
              </h1>
              <p className="rq-subtitle">Manage your connection requests 💖</p>
            </div>

            <div className="rq-search-wrap">
              <FaSearch className="rq-search-icon" />
              <input
                type="text"
                placeholder="Search by name, ID, location..."
                className="rq-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="rq-stats-grid">
            <div className="rq-stat-card">
              <div className="rq-stat-icon received"><FaInbox /></div>
              <div>
                <p className="rq-stat-value">{stats.received}</p>
                <p className="rq-stat-label">Received</p>
              </div>
            </div>
            <div className="rq-stat-card">
              <div className="rq-stat-icon sent"><FaPaperPlane /></div>
              <div>
                <p className="rq-stat-value">{stats.sent}</p>
                <p className="rq-stat-label">Sent</p>
              </div>
            </div>
            <div className="rq-stat-card">
              <div className="rq-stat-icon pending"><FaClock /></div>
              <div>
                <p className="rq-stat-value">{stats.pending}</p>
                <p className="rq-stat-label">Pending</p>
              </div>
            </div>
            <div className="rq-stat-card">
              <div className="rq-stat-icon accepted"><FaCheckCircle /></div>
              <div>
                <p className="rq-stat-value">{stats.accepted}</p>
                <p className="rq-stat-label">Accepted</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="rq-tabs">
            <button
              className={`rq-tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All
            </button>
            <button
              className={`rq-tab ${activeTab === 'received' ? 'active' : ''}`}
              onClick={() => setActiveTab('received')}
            >
              <FaInbox size={12} /> Received
              <span className="rq-tab-badge">{stats.received}</span>
            </button>
            <button
              className={`rq-tab ${activeTab === 'sent' ? 'active' : ''}`}
              onClick={() => setActiveTab('sent')}
            >
              <FaPaperPlane size={12} /> Sent
              <span className="rq-tab-badge">{stats.sent}</span>
            </button>
          </div>

          {/* Filters */}
          <div className="rq-filter-group">
            <button
              className={`rq-filter-btn ${filterBy === 'all' ? 'active' : ''}`}
              onClick={() => setFilterBy('all')}
            >
              All
            </button>
            <button
              className={`rq-filter-btn ${filterBy === 'pending' ? 'active' : ''}`}
              onClick={() => setFilterBy('pending')}
            >
              <FaClock size={11} /> Pending
            </button>
            <button
              className={`rq-filter-btn ${filterBy === 'accepted' ? 'active' : ''}`}
              onClick={() => setFilterBy('accepted')}
            >
              <FaCheck size={11} /> Accepted
            </button>
            <button
              className={`rq-filter-btn ${filterBy === 'rejected' ? 'active' : ''}`}
              onClick={() => setFilterBy('rejected')}
            >
              <FaTimes size={11} /> Rejected
            </button>
          </div>

          {/* Requests */}
          {filteredRequests.length > 0 ? (
            <div className="rq-grid">
              {filteredRequests.map((request) => {
                const status = getStatusBadge(request.status);
                const isReceived = request.type === 'received';
                const isLoading = actionLoading[request.id];
                const fromUserId = request.from_user;
                const toUserId = request.to_user;
                const otherUserId = isReceived ? fromUserId : toUserId;

                return (
                  <div key={request.id} className="rq-card">
                    {/* Top */}
                    <div className="rq-card-top">
                      <div className="rq-profile">
                        <div className="rq-avatar-wrap">
                          <img 
                            src={request.image || `https://ui-avatars.com/api/?name=${request.name}&background=ff0055&color=fff&size=128`} 
                            alt={request.name} 
                            className="rq-avatar"
                            onError={(e) => {
                              e.target.src = `https://ui-avatars.com/api/?name=${request.name}&background=ff0055&color=fff&size=128`;
                            }}
                          />
                          {request.online && <span className="rq-online-dot"></span>}
                        </div>
                        <div>
                          <p className="rq-name">{request.name}</p>
                          <p className="rq-bid">Profile ID: {request.profile_id ? `${request.profile_id}` : request.bid || 'N/A'}</p>
                          <div className="rq-online-row">
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: request.online ? '#4caf50' : '#bbb' }}></span>
                            <span className="rq-online-text" style={{ color: request.online ? '#4caf50' : '#999' }}>
                              {request.online ? 'Online' : 'Offline'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <span className={`rq-status ${status.class}`}>
                        {status.icon} {status.label}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="rq-details">
                      <span className="rq-detail"><FaUser size={12} /> {request.age || 0} Yrs, {request.height || 'N/A'}</span>
                      <span className="rq-detail"><FaMapMarkerAlt size={12} /> {request.location?.split(',')[0] || 'N/A'}</span>
                      <span className="rq-detail"><FaGraduationCap size={12} /> {request.education || 'N/A'}</span>
                      <span className="rq-detail"><FaBriefcase size={12} /> {request.occupation || 'N/A'}</span>
                      <span className="rq-detail"><FaClock size={12} /> {request.date || 'N/A'} • {request.time || ''}</span>
                      <span className="rq-detail heart"><FaHeart size={12} /> {request.matchPercentage || 70}% Match</span>
                    </div>

                    {/* Message */}
                    {request.message && (
                      <div className="rq-message">{request.message}</div>
                    )}

                    {/* Actions */}
                    <div className="rq-actions">
                      {/* ✅ RECEIVED + PENDING = Accept/Reject */}
                      {isReceived && request.status === 'pending' && (
                        <>
                          <button 
                            className="rq-action-btn rq-btn-accept" 
                            onClick={() => handleAccept(request.id)}
                            disabled={isLoading === 'accept'}
                          >
                            {isLoading === 'accept' ? (
                              <FaSpinner size={12} style={{ animation: 'spin 1s linear infinite' }} />
                            ) : (
                              <FaCheck size={12} />
                            )}
                            {isLoading === 'accept' ? 'Accepting...' : 'Accept'}
                          </button>
                          <button
                            className="rq-action-btn rq-btn-reject"
                            onClick={() => handleReject(request.id)}
                            disabled={isLoading === 'reject'}
                          >
                            {isLoading === 'reject' ? (
                              <FaSpinner size={12} style={{ animation: 'spin 1s linear infinite' }} />
                            ) : (
                              <FaTimes size={12} />
                            )}
                            {isLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                          </button>
                        </>
                      )}

                      {/* ✅ SENT + PENDING = Withdraw */}
                      {!isReceived && request.status === 'pending' && (
                        <button
                          className="rq-action-btn rq-btn-withdraw"
                          onClick={() => handleDelete(request.id)}
                          disabled={isLoading === 'delete'}
                        >
                          {isLoading === 'delete' ? (
                            <FaSpinner size={12} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <FaTrash size={12} />
                          )}
                          {isLoading === 'delete' ? 'Withdrawing...' : 'Withdraw'}
                        </button>
                      )}

                      {/* ✅ ACCEPTED = Chat Button */}
                      {request.status === 'accepted' && (
                        <button 
                          className="rq-action-btn rq-btn-chat"
                          onClick={() => handleStartChat(otherUserId)}
                        >
                          <FaComment size={12} /> Chat Now 💬
                        </button>
                      )}

                      {/* ✅ View Profile - Always visible */}
                      <button 
                        className="rq-action-btn rq-btn-view"
                        onClick={() => handleViewProfile(otherUserId)}
                      >
                        <FaEye size={12} /> View Profile
                      </button>

                      {/* ✅ Show status for completed requests */}
                      {request.status !== 'pending' && request.status !== 'accepted' && (
                        <button className="rq-action-btn rq-btn-outline" disabled>
                          <FaTimesCircle size={12} /> {request.status === 'rejected' ? 'Rejected' : 'Completed'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rq-empty">
              <div className="rq-empty-icon">
                <FaInbox />
              </div>
              <h3 className="rq-empty-title">No Requests Found</h3>
              <p className="rq-empty-sub">
                {activeTab === 'received'
                  ? "You haven't received any requests yet. 💖"
                  : activeTab === 'sent'
                    ? "You haven't sent any requests yet. 💌"
                    : "No requests match your search criteria. 🔍"}
              </p>
              {activeTab === 'received' && (
                <button className="rq-empty-btn" onClick={() => navigate('/all-users')}>
                  💖 Find Matches
                </button>
              )}
              {activeTab === 'sent' && (
                <button className="rq-empty-btn" onClick={() => navigate('/all-users')}>
                  💌 Send Requests
                </button>
              )}
            </div>
          )}

          {/* Pagination */}
          {filteredRequests.length > 0 && (
            <div className="rq-pagination">
              <div className="rq-page-info">
                Showing <strong>{filteredRequests.length}</strong> of <strong>{requestsData.length}</strong> requests
              </div>
              <div className="rq-page-btns">
                <button className="rq-page-btn"><FaArrowLeft size={10} /></button>
                <button className="rq-page-btn active">1</button>
                <button className="rq-page-btn">2</button>
                <button className="rq-page-btn"><FaArrowRight size={10} /></button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default Requests;